import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Upload,
  FileText,
  Download,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  File,
  FileImage,
  FileType
} from 'lucide-react';

const DocumentManagement = ({ documents, onDocumentUploaded, adminToken, backendUrl }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: '',
    access_level: 'basic',
    file: null
  });
  const [uploadMessage, setUploadMessage] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }

      // Check file type
      const allowedTypes = ['.pdf', '.doc', '.docx', '.txt', '.png', '.jpg', '.jpeg'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        alert(`File type not allowed. Supported types: ${allowedTypes.join(', ')}`);
        return;
      }

      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.title || !uploadForm.category) {
      alert('Please fill in all required fields and select a file');
      return;
    }

    setUploading(true);
    setUploadMessage('');

    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('access_level', uploadForm.access_level);

      console.log('Uploading document with admin token:', adminToken ? 'Token present' : 'No token');
      console.log('Upload URL:', `${backendUrl}/api/documents/upload`);
      
      const response = await fetch(`${backendUrl}/api/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData
      });

      console.log('Upload response status:', response.status);
      const result = await response.json();
      console.log('Upload response:', result);

      if (response.ok) {
        setUploadMessage('Document uploaded successfully!');
        setUploadForm({
          title: '',
          description: '',
          category: '',
          access_level: 'basic',
          file: null
        });
        
        // Reset file input
        const fileInput = document.getElementById('file-upload');
        if (fileInput) fileInput.value = '';

        // Trigger refresh
        if (onDocumentUploaded) {
          onDocumentUploaded();
        }
      } else {
        throw new Error(result.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // If the main upload fails with credentials error, try alternative method
      if (error.message.includes('Could not validate credentials') || error.message.includes('credentials')) {
        console.log('Trying alternative upload method...');
        setUploadMessage('Authentication failed, trying alternative method...');
        
        try {
          // Add admin email to form data for alternative endpoint
          const altFormData = new FormData();
          altFormData.append('file', uploadForm.file);
          altFormData.append('title', uploadForm.title);
          altFormData.append('description', uploadForm.description);
          altFormData.append('category', uploadForm.category);
          altFormData.append('access_level', uploadForm.access_level);
          altFormData.append('admin_email', 'admin@legacypropertyassociation.com');

          const altResponse = await fetch(`${backendUrl}/api/documents/upload-alt`, {
            method: 'POST',
            body: altFormData
          });

          const altResult = await altResponse.json();

          if (altResponse.ok) {
            setUploadMessage('Document uploaded successfully! (via alternative method)');
            setUploadForm({
              title: '',
              description: '',
              category: '',
              access_level: 'basic',
              file: null
            });
            
            // Reset file input
            const fileInput = document.getElementById('file-upload');
            if (fileInput) fileInput.value = '';

            // Trigger refresh
            if (onDocumentUploaded) {
              onDocumentUploaded();
            }
            return;
          } else {
            throw new Error(altResult.detail || 'Alternative upload failed');
          }
        } catch (altError) {
          console.error('Alternative upload error:', altError);
          setUploadMessage(`Upload failed: ${error.message}. Alternative method also failed: ${altError.message}`);
        }
      } else {
        setUploadMessage(`Upload failed: ${error.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId, title) => {
    if (!confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/documents/admin/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Document deleted successfully!');
        if (onDocumentUploaded) {
          onDocumentUploaded();
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Delete failed: ${error.message}`);
    }
  };

  const handleDownload = (documentId, filename) => {
    window.open(`${backendUrl}/api/documents/download/${documentId}`, '_blank');
  };

  const getFileIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-600" />;
      case 'doc':
      case 'docx':
        return <FileType className="w-5 h-5 text-blue-600" />;
      case 'png':
      case 'jpg':
      case 'jpeg':
        return <FileImage className="w-5 h-5 text-green-600" />;
      default:
        return <File className="w-5 h-5 text-gray-600" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'legal':
        return 'bg-red-100 text-red-800';
      case 'forms':
        return 'bg-blue-100 text-blue-800';
      case 'educational':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccessLevelColor = (accessLevel) => {
    switch (accessLevel?.toLowerCase()) {
      case 'basic':
        return 'bg-green-100 text-green-800';
      case 'standard':
        return 'bg-yellow-100 text-yellow-800';
      case 'premium':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Document Management</h2>
        <Button onClick={onDocumentUploaded} disabled={uploading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${uploading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Upload Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="w-5 h-5 text-blue-600" />
            <span>Upload New Document</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Title *
                </label>
                <Input
                  required
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter document title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <Select 
                  value={uploadForm.category} 
                  onValueChange={(value) => setUploadForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="legal">Legal Documents</SelectItem>
                    <SelectItem value="forms">Property Forms</SelectItem>
                    <SelectItem value="educational">Educational Resources</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Level *
                </label>
                <Select 
                  value={uploadForm.access_level} 
                  onValueChange={(value) => setUploadForm(prev => ({ ...prev, access_level: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select access level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic (All Members)</SelectItem>
                    <SelectItem value="standard">Standard (Standard + Premium)</SelectItem>
                    <SelectItem value="premium">Premium (Premium Only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File *
                </label>
                <Input
                  id="file-upload"
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Allowed: PDF, DOC, DOCX, TXT, PNG, JPG (Max 50MB)
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter document description (optional)"
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-4">
              <Button type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>

              {uploadMessage && (
                <div className={`flex items-center space-x-2 ${
                  uploadMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'
                }`}>
                  {uploadMessage.includes('successfully') ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">{uploadMessage}</span>
                </div>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>Uploaded Documents ({documents.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Access Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type & Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uploaded
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {getFileIcon(doc.file_type)}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                          {doc.description && (
                            <div className="text-sm text-gray-500">{doc.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge className={getCategoryColor(doc.category)}>
                        {doc.category?.charAt(0).toUpperCase() + doc.category?.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge className={getAccessLevelColor(doc.access_level)}>
                        {doc.access_level?.charAt(0).toUpperCase() + doc.access_level?.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium">{doc.file_type?.toUpperCase()}</div>
                        <div>{doc.file_size ? (doc.file_size / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(doc.id, doc.original_filename)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(doc.id, doc.title)}
                          className="text-red-600 hover:text-red-900 hover:border-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {documents.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No documents uploaded yet</p>
                <p className="text-sm">Upload your first document using the form above</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentManagement;